/**
 * Resolve which Xero organisation a venue should send bills to.
 * Prefer persisted defaultOrganizationId; never assume organizations[0]
 * unless that is the only option / no default is set.
 */

export function resolveXeroOrganization(xeroConnection = {}) {
  const organizations = Array.isArray(xeroConnection?.organizations)
    ? xeroConnection.organizations
    : []
  if (!organizations.length) return null

  const defaultId = xeroConnection?.defaultOrganizationId
  if (defaultId) {
    const match = organizations.find(org => org?.id === defaultId)
    if (match) return match
  }

  return organizations[0] || null
}

export function resolveXeroOrganizationId(xeroConnection = {}) {
  return resolveXeroOrganization(xeroConnection)?.id || null
}

/**
 * Dotted-path update so Mongo does not replace the whole xeroConnection
 * (which would wipe tokens). Also puts the destination org first for
 * older code that still reads organizations[0].
 */
export function buildXeroDefaultOrganizationUpdate(
  xeroConnection = {},
  organizationId,
) {
  const organizations = Array.isArray(xeroConnection?.organizations)
    ? [...xeroConnection.organizations]
    : []
  const orgId = String(organizationId || '')
  if (!orgId) {
    return {
      'xeroConnection.defaultOrganizationId': '',
    }
  }

  const matchIndex = organizations.findIndex(org => org?.id === orgId)
  let nextOrganizations = organizations
  if (matchIndex > 0) {
    const [match] = organizations.splice(matchIndex, 1)
    nextOrganizations = [match, ...organizations]
  }

  const update = {
    'xeroConnection.defaultOrganizationId': orgId,
  }
  if (matchIndex >= 0) {
    update['xeroConnection.organizations'] = nextOrganizations
  }
  return update
}

export function buildXeroFallbackAccountUpdate(account) {
  if (!account?.AccountID) {
    return {
      'xeroConnection.fallbackPurchasesAccountId': '',
      'xeroConnection.fallbackPurchasesAccountName': '',
    }
  }
  return {
    'xeroConnection.fallbackPurchasesAccountId': account.AccountID,
    'xeroConnection.fallbackPurchasesAccountName':
      account.Name || account.Code || account.AccountID,
  }
}

export function isXeroContactInOrganization(contactId, organization) {
  if (!contactId) return false
  const contacts = Array.isArray(organization?.contacts)
    ? organization.contacts
    : []
  return contacts.some(
    c => c?.ContactID === contactId || c?.contactID === contactId,
  )
}

export function isXeroAccountInOrganization(accountId, organization) {
  if (!accountId) return false
  const accounts = Array.isArray(organization?.accounts)
    ? organization.accounts
    : []
  return accounts.some(
    a => a?.AccountID === accountId || a?.accountID === accountId,
  )
}

export function getXeroMappingMismatch({
  defaultContact,
  defaultAccount,
  organization,
  contacts = null,
  accounts = null,
} = {}) {
  const orgContacts = contacts ?? organization?.contacts ?? []
  const orgAccounts = accounts ?? organization?.accounts ?? []
  const contactMismatch =
    Boolean(defaultContact) &&
    Array.isArray(orgContacts) &&
    orgContacts.length > 0 &&
    !orgContacts.some(
      c => c?.ContactID === defaultContact || c?.contactID === defaultContact,
    )
  const accountMismatch =
    Boolean(defaultAccount) &&
    Array.isArray(orgAccounts) &&
    orgAccounts.length > 0 &&
    !orgAccounts.some(
      a => a?.AccountID === defaultAccount || a?.accountID === defaultAccount,
    )
  return {
    contactMismatch,
    accountMismatch,
    hasMismatch: contactMismatch || accountMismatch,
  }
}

export const XERO_MAPPING_OTHER_ORG_WARNING =
  'This mapping is for another Xero company. Clear it and remap using contacts/accounts from the destination organisation.'

export const XERO_CONTACT_ARCHIVED_WARNING =
  'Archived in Xero. Use Force send to create the bill under a new active contact (e.g. Supplier (OMS)).'
